// Centralized SEO helpers for Reality Twin.
// All canonical / og:url values are absolute and self-referencing.
//
// No hreflang is emitted, and that is deliberate.
//
// It used to declare seven alternates per page pointing at ?lang=<code>. Every
// one of them was false: the server renders a single hardcoded <html lang="en">
// and the language only changes in a client-side effect after hydration, so a
// crawler fetching ?lang=de receives the English page. Those URLs also
// canonicalise back to the bare path, and hreflang pointing at non-canonical
// URLs is ignored outright. The result was ~490 declared alternates that served
// duplicate English content and did nothing.
//
// Declaring alternates that do not exist is worse than declaring none: it wastes
// crawl budget and tells search engines something untrue about the site. They
// come back when the pages are genuinely served per language — which needs
// per-locale URLs and request-scoped i18n, not a tag.

export const SITE_URL = "https://realitytwin.io";
export const SITE_NAME = "Reality Twin";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.jpg`;

export const LOCALES = ["en", "de", "es", "fr", "it", "ru", "zh"] as const;
export type Locale = (typeof LOCALES)[number];

export interface SeoOptions {
  /** Path starting with "/" — e.g. "/about". */
  path: string;
  title: string;
  description: string;
  /** Absolute or root-relative image URL. Defaults to the site OG image. */
  image?: string;
  /** og:type — defaults to "website". */
  type?: "website" | "article" | "product" | "profile";
  /** Set to true to hide the page from search engines. */
  noindex?: boolean;
}

export interface SeoHead {
  meta: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
}

/**
 * Build the per-route head() payload: title, description, canonical,
 * Open Graph, Twitter Card and hreflang alternates.
 */
export function buildSeo(opts: SeoOptions): SeoHead {
  const path = opts.path.startsWith("/") ? opts.path : `/${opts.path}`;
  const url = `${SITE_URL}${path === "/" ? "" : path}`;
  const image =
    opts.image && opts.image.startsWith("http")
      ? opts.image
      : opts.image
        ? `${SITE_URL}${opts.image.startsWith("/") ? opts.image : `/${opts.image}`}`
        : DEFAULT_OG_IMAGE;
  const type = opts.type ?? "website";

  const meta: Array<Record<string, string>> = [
    { title: opts.title },
    { name: "description", content: opts.description },
    {
      name: "robots",
      content: opts.noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    },
    { property: "og:title", content: opts.title },
    { property: "og:description", content: opts.description },
    { property: "og:type", content: type },
    { property: "og:url", content: url },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1216" },
    { property: "og:image:height", content: "640" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: opts.title },
    { name: "twitter:description", content: opts.description },
    { name: "twitter:image", content: image },
  ];

  // One canonical, self-referencing and absolute. Nothing else — see the note
  // at the top of this file on why hreflang is absent.
  const links: Array<Record<string, string>> = [{ rel: "canonical", href: url }];

  return { meta, links };
}
